import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSidebar } from '../../hooks/useSidebar'
import './index.scss'
import FlotiMap, { LAYER_TYPE } from '../../components/FlotiMap'
import History__sidebar from './sidebar'
import { initial_sidebar } from '../../contexts/SidebarContext/reducer'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useHighcharts } from '../../hooks/useHighcharts'
import { useTranslation } from 'react-i18next'
import { COLOR } from '../../data/variables'
import Infobox from '../../components/Infobox'
import { ICON } from '../../components/Icon/data'
import Button from '../../components/Button'
import Icon from '../../components/Icon'
import History__infobar from './infobar'
import { StopTrail, Trail, TrailInfo, useMap } from '../../hooks/useMap'
import { FlotiMapRef } from '../../components/FlotiMap/type'
import { DaterangeValue } from '../../components/Input/Daterange'
import { DateTime } from 'luxon'

const MOBILE_COLORS = [COLOR.MARKER_BLUE, COLOR.MARKER_RED, COLOR.MARKER_GREEN]
const STOP_DEFAULT_SCALE = 85

type Speed = {
  date: string
  id: number
  speed: number
  speedlimit: number
}

export type Stop = {
  date: string
  duration: number
  from_date: string
  heading: number
  id: number
  lat: number
  location: string
  lon: number
  to_date: string
}

const History = () => {
  const { t } = useTranslation()
  const sidebarRef = useRef()
  const mapRef = useRef<FlotiMapRef>()
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  const { updateSidebar, updateInfobar } = useSidebar()
  const { metersToKilometers } = useMap()

  const [speedData, setSpeedData] = useState<Speed[]>()
  const [selectedMobiles, setSelectedMobiles] = useState<string[]>([])
  const [stopData, setStopData] = useState<Stop[]>()
  const [chartFilter, setChartFilter] = useState<DaterangeValue | null>(null)
  const [savedTrail, setSavedTrail] = useState<Record<string, TrailInfo>>()
  const [fitInMap, setFitInMap] = useState<boolean>(false)
  const [isMapBusy, setIsMapBusy] = useState<boolean>(false)
  const [hoverTime, setHoverTime] = useState<string | null>(null)

  const series = useMemo(() => {
    const events = {
      mouseOver: function (this: Highcharts.Point) {
        setHoverTime(new Date(this.x).toISOString())
      },
      mouseOut: () => setHoverTime(null)
    }
    if (speedData) {
      const speed = {
        name: t('Speed'),
        color: COLOR.BLUE_70,
        data: [],
        point: {
          events
        }
      }

      const limit = {
        name: t('Speedlimit'),
        color: COLOR.RED_70,
        data: [],
        point: {
          events
        }
      }
      speedData.sort((a, b) => a.date > b.date ? 1 : -1).forEach((s) => {
        speed.data.push([s.date, s.speed])
        limit.data.push([s.date, s.speedlimit])
      })
      return [speed, limit]
    }
    return []
  }, [speedData])

  const onChartSelection = (e: Highcharts.SelectEventObject) => {
    if (e.xAxis) {
      const { min, max } = e.xAxis[0]
      setChartFilter({
        from: new Date(min).toISOString(),
        to: new Date(max).toISOString()
      })
    } else if (e.resetSelection) setChartFilter(null)
  }

  const { options } = useHighcharts({
    title: t('Speed'),
    axis: {
      xType: 'datetime',
      yAxisTitle: t('Speed'),
      xFormat: '{value:%H:%M}'
    },
    tooltip: t('km/h'),
    series,
    events: {
      selection: (e: Highcharts.SelectEventObject) => onChartSelection(e)
    }
  })

  const fitTrailMap = useCallback(() => {
    if (!isMapBusy && series?.length) {
      const didFit = mapRef.current?.fitInMap(LAYER_TYPE.MARKERS)
      setFitInMap(!didFit)
    } else {
      setFitInMap(true) // Fit when map is not busy
    }
  }, [isMapBusy, mapRef.current, series])


  useEffect(() => {
    if (fitInMap && !isMapBusy) {
      fitTrailMap()
    }
  }, [fitInMap, isMapBusy])

  const setTrailInfo = useCallback((trailInfo: Record<string, TrailInfo>) => {
    chartRef.current?.chart.zoomOut()
    setChartFilter(null)
    setSelectedMobiles(Object.keys(trailInfo))
    setSavedTrail(trailInfo)
  }, [])

  const trailData = useMemo(() => {
    const info = savedTrail ?? {}
    const combinedPoints = Object.keys(info).reduce((arr, mobile, idx) => {
      const color = MOBILE_COLORS[idx % MOBILE_COLORS.length] // Circle colors around
      const mappedTrail = info[mobile].trail.map((point) => {
        const date = DateTime.fromISO(point.date).toFormat('dd/LL/yyyy HH:mm:ss')
        return {
          ...point,
          color,
          trailId: mobile,
          overlay: `<strong>${mobile}:</strong> ${date}`,
        }
      })
      return arr.concat(mappedTrail)
    }, [])

    const filteredPoints = !chartFilter ? combinedPoints : combinedPoints.filter((point) => {
      const { from, to } = chartFilter
      return point.date >= from && point.date <= to
    })

    const trailPoints = filteredPoints as Trail[]
    const distance = trailPoints.length > 1
      ? trailPoints[trailPoints.length - 1].odometer - trailPoints[0].odometer
      : 0

    const combinedStops = Object.keys(info).reduce((arr, mobile) => {
      return arr.concat(info[mobile].stops)
    }, [] as StopTrail[])

    const filteredStops = !chartFilter ? combinedStops : combinedStops.filter((stop) => {
      const { from, to } = chartFilter
      return stop?.from_date >= from && stop.from_date <= to
    })

    const mapStops = filteredStops.map((stop) => {
      const numDuration = stop.duration > 60 ? stop.duration / 60 : stop.duration
      const duration = stop.duration > 60 ? `${Math.floor(numDuration)}h` : `${numDuration}m`

      const date = DateTime.fromISO(stop?.from_date).toFormat('dd/LL/yyyy HH:mm:ss')
      return {
        icon: ICON.STOP_SIGN,
        color: COLOR.RED_70,
        text: duration,
        lat: stop.lat,
        lon: stop.lon,
        scale: STOP_DEFAULT_SCALE,
        overlay: `${stop.mobile} ${date}: <strong>${duration}</strong>`
      }
    })

    return { info, filteredPoints, mapStops, distance: metersToKilometers(distance) }
  }, [savedTrail, chartFilter])

  useEffect(() => {
    const { info, filteredPoints } = trailData
    mapRef.current?.setPoints(filteredPoints)
    if (Object.keys(info).length) {
      const mobile = Object.keys(info)[0]
      setSpeedData(info[mobile].speed)
      setStopData(info[mobile].stops)
    }

    if (filteredPoints.length) {
      setFitInMap(true)
    }
  }, [trailData])

  // Cheap lookup against the already-built trail instead of rebuilding it on hover
  const nowPoint = useMemo(() => {
    if (!hoverTime) return undefined
    return (trailData.filteredPoints as Trail[]).find((point) => new Date(point.date).toISOString() === hoverTime)
  }, [trailData.filteredPoints, hoverTime])

  useEffect(() => {
    const icons = nowPoint ? [...trailData.mapStops, { ...nowPoint, icon: ICON.MAP_MARKER, scale: 50 }] : trailData.mapStops
    mapRef.current?.setIcons(icons)
  }, [trailData.mapStops, nowPoint])

  useEffect(() => {
    updateSidebar({
      content: <History__sidebar ref={sidebarRef} mapRef={mapRef} setTrailInfo={setTrailInfo} />,
      isOpen: true,
      width: 660
    })

    return () => {
      updateSidebar(initial_sidebar)
    }
  }, [])

  const infoboxMsg = useMemo(() => {
    return (
      <span>
        <strong>{`${t('Tip')}: `}</strong>
        {t('If you want to zoom in a specific section of the graph, click and drag from left to right.')}
      </span>
    )
  }, [t])

  const openInfobar = useCallback((type: 'collisions' | 'stops') => {
    const filters = sidebarRef.current.getFilters()
    const mobile = selectedMobiles.join(' ')
    const title = type === 'collisions' ? t('Collisions for') : t('Stops for')
    updateInfobar({
      header: (
        <span>
          <strong>{`${title}: `}</strong>
          {mobile}
        </span>
      ),
      isOpen: true,
      width: 660,
      content: (
        <History__infobar
          type={type}
          mobiles={filters.mobiles}
          dates={filters.dates}
          stops={stopData}
        />
      )
    })
  }, [t, selectedMobiles, sidebarRef, stopData])

  const buttons = {
    /* DOWNLOAD: {
      label: t('Download XLS'),
      icon: ICON.DOCUMENT_DOWNLOAD,
      type: 'secondary'
    }, */
    STOPS: {
      label: t('Show stops'),
      icon: ICON.SEARCH_DATA,
      type: 'tertiary',
      onClick: () => openInfobar('stops')
    }, 
    COLLISIONS: {
      label: t('Show collisions'),
      icon: ICON.WARNING_ALT,
      type: 'tertiary',
      onClick: () => openInfobar('collisions')
    }
  }

  return (
    <div className={`History${speedData?.length && selectedMobiles.length === 1 ? ' open' : ''}`}>
      <FlotiMap
        ref={mapRef}
        showTrails={true}
        isBusyCallback={(val: boolean) => setIsMapBusy(val)}
        layerOptions={{
          [LAYER_TYPE.MARKERS]: { minZoom: 13 }
        }}
        options={{
          markerScale: 20,
          showClusterIcons: true
        }}
      />
      {speedData?.length && selectedMobiles.length === 1 && (
        <div className="History__speed">
          <div className="History__buttons">
            {Object.keys(buttons).map((key) => {
              const { type, label, icon, onClick } = buttons[key]
              return (
                <Button
                  type={type}
                  size='m'
                  onClick={onClick}
                  label={(
                    <>
                      {label}
                      <Icon icon={icon} color={type === 'secondary' ? COLOR.WHITE : COLOR.TEXT_GRAY} />
                    </>
                  )}
                />
              )
            })}
          </div>
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={options}
          />
          <div className="History__distance">
            {t('Distance: {{distance}}km', { distance: trailData.distance })}
          </div>
          <Infobox type="inline" status="info" message={infoboxMsg} closable={true} />
        </div>
      )}
    </div>
  )
}

export default History
